import { useState } from "react";
import api, { formatApiError } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, MailCheck } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPasswordDialog({ open, onOpenChange }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", {
        email,
        origin: window.location.origin,
      });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const close = (o) => {
    if (!o) {
      setSent(false);
      setEmail("");
    }
    onOpenChange(o);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reset your password</DialogTitle>
          <DialogDescription>
            {sent
              ? "Check your inbox for the reset link."
              : "Enter your account email and we'll send you a reset link."}
          </DialogDescription>
        </DialogHeader>
        {sent ? (
          <div className="flex flex-col items-center gap-2 py-4 text-center" data-testid="forgot-sent">
            <MailCheck className="h-10 w-10 text-primary" />
            <p className="text-sm text-slate-600">
              If an account exists for <span className="font-semibold">{email}</span>, a password
              reset link is on its way. The link expires in 1 hour.
            </p>
            <Button className="mt-2 h-11 w-full" onClick={() => close(false)} data-testid="forgot-done">
              Done
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@steriorenewables.ie"
                className="h-11"
                data-testid="forgot-email"
                required
              />
            </div>
            <DialogFooter>
              <Button type="submit" className="h-11 w-full" disabled={loading} data-testid="forgot-submit">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Send reset link
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
